import { api } from "./client";

export interface Plan {
  id: string;
  name: string;
  max_devices: number;
  max_businesses: number;
  trial_duration_days: number;
  price_monthly: number;
  price_yearly: number;
  original_price_monthly?: number;
  original_price_yearly?: number;
  features: Record<string, unknown>;
  is_public: boolean;
  custom_price_monthly?: number;
  custom_price_yearly?: number;
  custom_max_devices?: number;
  price_per_additional_outlet?: number;
  type: string;
}

export interface OutletAddonOrder {
  gateway_order_id: string;
  amount: number; // paise
  currency: string;
  key_id: string;
  valid_until: string;
}

export interface SubscriptionStatus {
  status: "trial" | "active" | "expired" | "cancelled";
  plan_id?: string;
  plan_name?: string;
  expires_at?: string;
  days_remaining: number;
  max_devices: number;
  max_businesses: number;
  current_devices: number;
  features: Record<string, unknown>;
}

export interface PaymentRecord {
  id: string;
  invoice_id?: string;
  status: string;
  amount: number;
  billing_cycle: string;
  utr_number?: string;
  created_at: string;
}

export interface RazorpayOrder {
  gateway_order_id: string;
  amount: number; // paise
  currency: string;
  key_id: string;
}

export interface InvoiceLineItem {
  description: string;
  amount: number;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  billing_period_start: string;
  billing_period_end: string;
  line_items: InvoiceLineItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  status: string;
  paid_at?: string;
  created_at: string;
}

export const subscriptionApi = {
  getStatus(): Promise<SubscriptionStatus> {
    return api.get<SubscriptionStatus>("/subscription/");
  },

  getPlans(): Promise<{ plans: Plan[] }> {
    return api.get<{ plans: Plan[] }>("/plans");
  },

  createPayment(body: { plan_id: string; billing_cycle: string }): Promise<PaymentRecord> {
    return api.post<PaymentRecord>("/subscription/payment", body);
  },

  updatePaymentUtr(paymentId: string, utr: string): Promise<void> {
    return api.put<void>(`/subscription/payment/${paymentId}/utr`, { utr });
  },

  createOrder(body: { plan_id: string; billing_cycle: string }): Promise<RazorpayOrder> {
    return api.post<RazorpayOrder>("/subscription/payment/order", body);
  },

  verifyPayment(body: {
    gateway_order_id: string;
    gateway_payment_id: string;
    signature: string;
  }): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>("/subscription/payment/verify", body);
  },

  getInvoices(): Promise<Invoice[]> {
    return api.get<Invoice[]>("/subscription/invoices");
  },

  /** Create a prorated Razorpay order for one additional outlet slot */
  createOutletAddonOrder(): Promise<OutletAddonOrder> {
    return api.post<OutletAddonOrder>("/subscription/payment/outlet-order", {});
  },

  /** Verify Razorpay payment for an outlet add-on and activate the slot */
  verifyOutletAddon(body: {
    gateway_order_id: string;
    gateway_payment_id: string;
    signature: string;
  }): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>("/subscription/payment/outlet-verify", body);
  },
};
